/**
 * @typedef {Object.<string, any>} PlainObject
 * A plain JavaScript object representing the logical state of a Storage.
 */

/**
 * Transformer responsible for converting between a Web Storage–compatible
 * object and a plain JavaScript object representation.
 *
 * This transformer operates strictly on the public Web Storage API:
 * - `length`
 * - `key(index)`
 * - `getItem(key)`
 * - `setItem(key, value)`
 * - `removeItem(key)`
 * - `clear()`
 *
 * No assumptions are made about the underlying storage implementation
 * (e.g. localStorage, sessionStorage, in-memory storage, decorated storage).
 *
 * The transformation is structural and reversible:
 * - Converting a Storage to a PlainObject captures its logical state
 * - Applying a PlainObject to a Storage restores that state
 *
 * This transformer does NOT perform:
 * - JSON serialization
 * - Persistence
 * - Validation
 * - Encryption
 *
 * Those concerns must be handled by higher-level components (e.g. lenses).
 */
const storagePlainObjectTransformer = {
  /**
   * Converts a Storage into a plain JavaScript object.
   *
   * Each key/value pair in the storage is copied into the resulting object
   * using the storage keys as object properties.
   *
   * @param {Storage} storage
   * A Web Storage–compatible object to read from.
   *
   * @returns {PlainObject}
   * A plain object representing the logical state of the storage.
   */
  toObject(storage) {
    const result = {};

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      result[key] = storage.getItem(key);
    }

    return result;
  },

  /**
   * Applies a plain JavaScript object to a Storage.
   *
   * The storage is cleared before applying the new state to ensure that
   * the resulting storage exactly matches the provided object.
   *
   * @param {PlainObject} object
   * A plain object representing a logical storage state.
   *
   * @param {Storage} storage
   * A Web Storage–compatible object to write into.
   *
   * @returns {Storage}
   * The same storage instance, after being updated.
   */
  fromObject(object, storage) {
    storage.clear();

    Object.entries(object).forEach(([key, value]) => {
      storage.setItem(key, value);
    });

    return storage;
  },
};

module.exports = storagePlainObjectTransformer;
